import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "@/contexts/AppContext";
import { ArrowRight, ArrowLeft, Check, Loader2, Sparkles, Search } from "lucide-react";
import logoText from "@/assets/logo-text-transparent.png";

const GENDERS = ["Male", "Female", "Other"];
const INTERESTS = [
  "Music", "Gaming", "Sports", "Movies", "Travel", "Food", "Fashion", "Art", "Tech", "Fitness",
  "Books", "Photography", "Dancing", "Cooking", "Comedy", "Anime", "Cars", "Nature", "Pets",
  "Memes", "K-Pop", "Hip-Hop", "Rock", "EDM", "Football", "Basketball", "Chess", "Coffee",
];

const GuestOnboardingPage = () => {
  const { completeGuestOnboarding } = useApp();
  const [step, setStep] = useState(0);
  const [gender, setGender] = useState("");
  const [search, setSearch] = useState("");
  const [interests, setInterests] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const toggle = (i: string) => {
    setInterests(prev => prev.includes(i) ? prev.filter(x => x !== i) : prev.length >= 8 ? prev : [...prev, i]);
  };

  const filtered = INTERESTS.filter(i => i.toLowerCase().includes(search.toLowerCase()));

  const finish = async () => {
    setSubmitting(true);
    try {
      await completeGuestOnboarding({ gender, interests });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col items-center px-6 py-8 overflow-y-auto relative">
      <div className="absolute top-10 -right-20 w-60 h-60 rounded-full bg-primary/[0.05] blur-[100px] pointer-events-none" />
      <div className="absolute bottom-10 -left-20 w-60 h-60 rounded-full bg-accent/[0.05] blur-[100px] pointer-events-none" />

      <div className="w-full max-w-sm relative z-10 flex-1 flex flex-col">
        <div className="w-full flex items-center gap-1.5 mb-8">
          {[0, 1].map(i => (
            <div key={i} className={`flex-1 h-1.5 rounded-full transition-all ${i <= step ? "gradient-premium" : "bg-muted/30"}`} />
          ))}
        </div>

        <img src={logoText} alt="TalkLot" className="h-10 mb-6 object-contain self-center" decoding="async" />

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="gender" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="flex-1 flex flex-col">
              <h1 className="text-2xl font-display font-bold text-foreground mb-2">Quick question</h1>
              <p className="text-muted-foreground text-sm mb-6">Helps us match you with the right people.</p>
              <label className="text-xs text-muted-foreground mb-2 block">I am</label>
              <div className="grid grid-cols-3 gap-2 mb-2">
                {GENDERS.map(g => (
                  <button key={g} onClick={() => setGender(g)}
                    className={`py-3 rounded-xl text-sm font-medium ${gender === g ? "gradient-premium text-primary-foreground" : "bg-muted/30 text-muted-foreground border border-border/15"}`}>
                    {g}
                  </button>
                ))}
              </div>
              <div className="flex-1" />
              <motion.button whileTap={{ scale: 0.97 }} onClick={() => setStep(1)} disabled={!gender}
                className="w-full py-3.5 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm disabled:opacity-30 flex items-center justify-center gap-2 mt-6">
                Continue <ArrowRight className="w-4 h-4" />
              </motion.button>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="interests" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="flex-1 flex flex-col">
              <div className="flex items-baseline justify-between mb-2">
                <h1 className="text-2xl font-display font-bold text-foreground">Your vibes</h1>
                <span className="text-xs font-medium text-primary">{interests.length}/8</span>
              </div>
              <p className="text-muted-foreground text-sm mb-4">Pick a few to get better matches.</p>

              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
                <input type="text" placeholder="Search interests…" value={search} onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-muted/30 border border-border/15 text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/40" />
              </div>

              <div className="flex flex-wrap gap-2 pb-4">
                {filtered.map(i => {
                  const sel = interests.includes(i);
                  return (
                    <button key={i} onClick={() => toggle(i)}
                      className={`px-3.5 py-2 rounded-full text-xs font-medium transition-all ${sel ? "gradient-premium text-primary-foreground shadow-md" : "bg-muted/30 text-muted-foreground border border-border/15"}`}>
                      {sel && <Check className="inline w-3 h-3 mr-1" />}{i}
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-2 mt-auto pt-4 sticky bottom-0 bg-background/80 backdrop-blur-sm">
                <button onClick={() => setStep(0)} className="px-4 py-3 rounded-xl bg-muted/20 text-muted-foreground"><ArrowLeft className="w-4 h-4" /></button>
                <motion.button whileTap={{ scale: 0.97 }} onClick={finish} disabled={submitting || interests.length === 0}
                  className="flex-1 py-3 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm disabled:opacity-30 flex items-center justify-center gap-2">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Enter TalkLot
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GuestOnboardingPage;